import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

const log = createLogger('useSchoolBehaviorReportQuery');

export const QUERY_KEY = 'schoolBehaviorReport';

/** Relative-range presets matching the teacher endpoints' `ReportRange` substrate
 *  (`Nd`/`Nw`/`Nm`). Backend resolves these to absolute start/end internally. */
export type TimeRange = '7d' | '30d' | '90d' | '365d';

export type BehaviorSeries = {
  key: string;
  label: string;
  data: (number | null)[];
};

/** Wire shape from `/report/getSchoolBehaviorReport`. `ticks` are raw ISO dates
 *  (`"2026-05-19"`); `BehaviorChartView` formats and thins them for display. */
export type SchoolBehaviorReportResponse = {
  interval: string;
  ticks: string[];
  series: BehaviorSeries[];
  periodCount: number;
  sessionCount: number;
};

export function useSchoolBehaviorReportQuery({ timeRange }: { timeRange: TimeRange }) {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, error, status, isFetching } = useQuery<SchoolBehaviorReportResponse>({
    queryKey: [QUERY_KEY, timeRange],
    queryFn: async () => {
      const tz = locale?.timeZone ?? '';
      log.debug('Fetching school behavior report', { timeRange, tz });
      const params = new URLSearchParams({ range: timeRange, tz });
      const response = await httpClient.get<SchoolBehaviorReportResponse>(`/report/getSchoolBehaviorReport?${params}`);
      log.info('Fetched school behavior report successfully', {
        periodCount: response.periodCount,
        sessionCount: response.sessionCount,
        tickCount: response.ticks.length,
      });
      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch school behavior report', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status, isFetching };
}
